"use client";

import { LogOut, Menu, X } from "lucide-react";
import Link from "next/link";
import { useState } from "react";

import { useUser } from "@/hooks/useUser";
import { cn } from "@/lib/utils";

import { WordmarkLogo } from "@/components/logo/wordmark";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Icons } from "@masterkey/ui/icons";

export const MainNavbar = () => {
  const { user, avatarUrl, handleSignIn, handleSignOut } = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const mobileMenuContent = (
    <div
      className={cn(
        "absolute left-0 right-0 top-full z-40 mt-4 w-full overflow-hidden rounded-lg border border-border/10 bg-background p-4 shadow-lg backdrop-blur-md transition-all duration-300 ease-in-out md:hidden",
        isMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0",
      )}
    >
      <div className="container">
        <div className="flex flex-col gap-4 py-6">
          {user ? (
            <>
              <Button
                onClick={() => {
                  handleSignOut();
                  toggleMenu();
                }}
                variant="outline"
                className="flex w-full items-center justify-center gap-2 font-semibold"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </Button>
            </>
          ) : (
            <>
              <Link href="/login" className="w-full" onClick={toggleMenu}>
                <Button
                  onClick={handleSignIn}
                  variant="outline"
                  className="w-full font-semibold border-2 text-primary border-primary/30 hover:border-primary/70 hover:bg-primary/5 hover:text-primary"
                >
                  Sign In
                </Button>
              </Link>
              <Link
                href="/login?signup=true"
                className="w-full"
                onClick={toggleMenu}
              >
                <Button variant="default" className="w-full font-semibold">
                  Sign Up
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const desktopActionButtons = user ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <UserAvatar avatarUrl={avatarUrl} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleSignOut}>
          <Icons.SignOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <>
      <Link href="/login">
        <Button
          onClick={handleSignIn}
          variant="outline"
          className="rounded-lg border-2 font-semibold text-primary border-gray-300 hover:border-primary/70 hover:bg-primary/5 hover:text-primary"
        >
          Login
        </Button>
      </Link>
      <Link href="/login?signup=true">
        <Button
          variant="default"
          className="rounded-lg bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Sign Up
        </Button>
      </Link>
    </>
  );

  return (
    <>
      <header
        className={cn(
          "fixed left-1/2 top-4 z-50 w-[calc(100%-2rem)] max-w-5xl -translate-x-1/2",
          "flex items-center justify-between rounded-lg border border-border/10 bg-background/80 px-4 py-3 shadow-lg backdrop-blur-md transition-all duration-300 md:py-2",
        )}
      >
        <Link href="/" className="flex flex-shrink-0 items-center">
          <WordmarkLogo className="w-24 h-auto" color="hsl(var(--primary))" />
        </Link>

        <div className="hidden flex-shrink-0 items-center gap-3 md:flex">
          {desktopActionButtons}
        </div>

        <button
          className="ml-auto text-foreground md:hidden"
          onClick={toggleMenu}
          type="button"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {mobileMenuContent}
      </header>
    </>
  );
};
